/**
 * useLibraryComponents Hook
 * 
 * Centralized hook for accessing UI components from @int-traderjoes/ui-components-library
 * This provides a single point of access for all library components across the application
 * 
 * Benefits:
 * - Centralized import management
 * - Consistent component access
 * - Easy to update if library structure changes
 * - Better tree-shaking optimization
 * - Type safety
 * 
 * Usage:
 * ```tsx
 * import { useLibraryComponents } from '@/hooks/useLibraryComponents';
 * 
 * const MyComponent = () => {
 *   const { Button, FormBox, showToast, FileUploadDisplay } = useLibraryComponents();
 *   
 *   return (
 *     <FormBox>
 *       <FileUploadDisplay
 *         hasUploadedFile={false}
 *         onFileChange={handleFileChange}
 *         onDownload={() => {}}
 *         onDelete={() => {}}
 *       />
 *       <Button onClick={() => showToast({ message: 'Success!', type: 'success' })}>
 *         Click me
 *       </Button>
 *     </FormBox>
 *   );
 * };
 * ```
 */

import { useMemo } from 'react';

// Import all commonly used components from the library
import {
  // Form Components
  Button,
  FormBox,
  Input,
  Label,
  Dropdown,
  FileInput,
  Textarea,
  Checkbox,
  Radio,
  AutoComplete,
  CustomDatePicker,
  
  // UI Components
  IconButton,
  Loader,
  Card,
  
  // Utility Functions
  showToast,
} from "@int-traderjoes/ui-components-library";

// Import custom components that will be moved to library later
import FileUploadDisplay from "@/components/Common/FileUploadDisplay";

/**
 * Interface defining all available library components
 */
export interface LibraryComponents {
  // Form Components
  Button: typeof Button;
  FormBox: typeof FormBox;
  Input: typeof Input;
  Label: typeof Label;
  Dropdown: typeof Dropdown;
  FileInput: typeof FileInput;
  Textarea: typeof Textarea;
  Checkbox: typeof Checkbox;
  Radio: typeof Radio;
  AutoComplete: typeof AutoComplete;
  CustomDatePicker: typeof CustomDatePicker;
  
  // UI Components
  IconButton: typeof IconButton;
  Loader: typeof Loader;
  Card: typeof Card;
  
  // Custom Components (will be moved to library)
  FileUploadDisplay: typeof FileUploadDisplay;
  
  // Utility Functions
  showToast: typeof showToast;
}

/**
 * Centralized hook for accessing UI library components
 * 
 * @returns {LibraryComponents} Object containing all library components
 */
export const useLibraryComponents = (): LibraryComponents => {
  return useMemo(() => ({
    // Form Components
    Button,
    FormBox,
    Input,
    Label,
    Dropdown,
    FileInput,
    Textarea,
    Checkbox,
    Radio,
    AutoComplete,
    CustomDatePicker,
    
    // UI Components
    IconButton,
    Loader,
    Card,
    
    // Custom Components (will be moved to library)
    FileUploadDisplay,
    
    // Utility Functions
    showToast,
  }), []);
};

/**
 * Individual component hooks for specific use cases
 * These can be used when you only need specific components
 */

/**
 * Hook for form-related components only
 */
export const useFormComponents = () => {
  const components = useLibraryComponents();
  
  return useMemo(() => ({
    Button: components.Button,
    FormBox: components.FormBox,
    Input: components.Input,
    Label: components.Label,
    Dropdown: components.Dropdown,
    FileInput: components.FileInput,
    Textarea: components.Textarea,
    Checkbox: components.Checkbox,
    Radio: components.Radio,
    AutoComplete: components.AutoComplete,
    CustomDatePicker: components.CustomDatePicker,
  }), [components]);
};

/**
 * Hook for UI components only
 */
export const useUIComponents = () => {
  const components = useLibraryComponents();
  
  return useMemo(() => ({
    IconButton: components.IconButton,
    Loader: components.Loader,
    Card: components.Card,
    Button: components.Button,
    FormBox: components.FormBox,
    FileUploadDisplay: components.FileUploadDisplay,
  }), [components]);
};

/**
 * Hook for utility functions only
 */
export const useLibraryUtils = () => {
  const components = useLibraryComponents();
  
  return useMemo(() => ({
    showToast: components.showToast,
  }), [components]);
};


/**
 * Default export for convenience
 */
export default useLibraryComponents;
